import {match, Pattern} from 'ts-pattern';
import {z} from 'zod';

import {isNil} from 'ramda';

import type {GridPdfExportContext} from 'features/eag-grid';
import {PdfExportError} from 'features/pdf-export';

import {SERVICE_ORDER_PDF_COLUMNS} from '../constants/SERVICE_ORDER_PDF_COLUMNS';

const EMPTY_VALUE = '-';
const decimalSchema = z.union([
  z.number().finite(),
  z
    .string()
    .trim()
    .min(1)
    .refine((value) => Number.isFinite(Number(value)))
    .transform(Number),
]);
const cellSchema = z.object({
  value: z
    .union([
      z.string(),
      z.number().finite(),
      z.object({label: z.string().nullable()}),
      z.object({amount: decimalSchema.nullable(), currency: z.string()}),
    ])
    .nullable(),
});

type PdfFormatters = {
  dateTime: (value: string) => string;
  number: (value: number) => string | null;
  currency: (amount: number, code: string) => string | null;
  fuel: (value: number) => string;
  mileageUnit: string;
};

/** Fixed service-order layout, using only the selected grid snapshot. */
export function createServiceOrderPdfData(context: GridPdfExportContext, format: PdfFormatters) {
  if (context.rows.length === 0) {
    throw new PdfExportError('general.notifications.noItemsSelected');
  }
  const titles = new Map(context.columns.map((column) => [column.key, column.title]));
  return context.rows.map((row) => {
    const field = (key: (typeof SERVICE_ORDER_PDF_COLUMNS)[number]) => {
      try {
        const label = titles.get(key);
        if (isNil(label)) {
          throw new Error(`Missing service-order PDF column: ${key}`);
        }
        const value = cellSchema.parse(row.cells[key]).value;
        const text = match(value)
          .with(Pattern.string, (text) => {
            if (text.length === 0) {
              return EMPTY_VALUE;
            }
            return ['createdAt', 'openedAt', 'closedAt'].includes(key)
              ? format.dateTime(text)
              : text;
          })
          .with(Pattern.number, (number) =>
            match(key)
              .with('fuelTank', () => format.fuel(number))
              .with('actualMileage', () => `${format.number(number)} ${format.mileageUnit}`)
              .otherwise(() => format.number(number))
          )
          .with({label: Pattern.union(Pattern.string, Pattern.nullish)}, ({label}) => label)
          .with({amount: Pattern.number, currency: Pattern.string}, ({amount, currency}) =>
            format.currency(amount, currency)
          )
          .otherwise(() => EMPTY_VALUE);
        return {key, label, value: text || EMPTY_VALUE};
      } catch (cause) {
        throw new PdfExportError(
          'module.reportDocument.labels.dataUnavailable',
          false,
          'entity.integration.labels.pleaseRefresh',
          {cause}
        );
      }
    };

    return {
      id: row.id,
      number: field('number'),
      branch: field('branch'),
      orderType: field('orderType'),
      orderVariant: field('orderVariant'),
      orderState: field('orderState'),
      vehicle: field('vehicle'),
      vehicleLicencePlate: field('vehicleLicencePlate'),
      vehicleVin: field('vehicleVin'),
      actualMileage: field('actualMileage'),
      fuelTank: field('fuelTank'),
      customerName: field('customerName'),
      totalPriceWithoutVat: field('totalPriceWithoutVat'),
      totalPriceWithVat: field('totalPriceWithVat'),
      createdAt: field('createdAt'),
      openedAt: field('openedAt'),
      closedAt: field('closedAt'),
      requests: field('requests'),
    };
  });
}
